import $ from "jquery";

import { SITE } from "../config";
import { pageLang, t } from "../i18n/ui";

interface Recaptcha {
  render(el: HTMLElement, options: { sitekey: string; theme?: "light" | "dark" }): number;
  getResponse(id?: number): string;
  reset(id?: number): void;
}
declare global {
  interface Window {
    grecaptcha?: Recaptcha;
    otridonRecaptcha?: () => void;
  }
}

const HL: Record<string, string> = { zh: "zh-CN", pt: "pt-PT" };

export function initContact(): void {
  const $form = $<HTMLFormElement>("[data-contact]");
  const $status = $form.find("[data-contact-status]");
  if (!$form.length || !$status.length) return;
  const field = (name: string) => $form.find<HTMLInputElement>(`[name="${name}"]`);
  const value = (name: string) => String(field(name).val() ?? "").trim();
  const say = (text: string) => $status.text(text);

  const $check = $form.find("[data-recaptcha]");
  const box = $check.find<HTMLElement>("[data-recaptcha-box]").get(0);
  const checked = !!(SITE.recaptchaSiteKey && box);
  let widget: number | null = null;
  let loading = false;
  if (checked) $check.prop("hidden", false);

  const render = () => {
    if (!window.grecaptcha || !box) return;
    const el = document.createElement("div");
    box.replaceChildren(el);
    widget = window.grecaptcha.render(el, { sitekey: SITE.recaptchaSiteKey, theme: "light" });
  };

  const load = () => {
    if (!checked || loading) return;
    loading = true;
    window.otridonRecaptcha = () => {
      render();
      $check.addClass("is-ready");
    };
    const lang = pageLang();
    $("<script>")
      .attr({
        src: `https://www.google.com/recaptcha/api.js?onload=otridonRecaptcha&render=explicit&hl=${HL[lang] ?? lang}`,
        async: true,
        defer: true,
      })
      .on("error", () => {
        loading = false;
        say(t("The security check could not load. Check your connection, or use Email us under Contact."));
      })
      .appendTo("head");
  };
  $form.on("focusin pointerdown", load);

  const token = () => (widget !== null && window.grecaptcha ? window.grecaptcha.getResponse(widget) : "");
  const resetCheck = () => {
    if (widget !== null) window.grecaptcha?.reset(widget);
  };

  const invalid = ($el: JQuery<HTMLElement>, message: string) => {
    $el.attr("aria-invalid", "true").trigger("focus");
    say(message);
  };
  $form.on("input", "input, textarea", function () {
    $(this).removeAttr("aria-invalid");
  });

  $form.on("submit", (e) => {
    e.preventDefault();
    if ($form.attr("data-busy")) return;
    if (!value("name")) return invalid(field("name"), t("Enter your name and surname."));
    if (!value("surname")) return invalid(field("surname"), t("Enter your name and surname."));
    const email = field("email");
    if (!value("email") || !(email.get(0)?.checkValidity() ?? false))
      return invalid(email, t("Enter an email we can reply to, like name@example.com."));
    if (!SITE.contactEndpoint) {
      say(t("The form is not connected yet. Use Email us under Contact and we will get back to you within 24 hours."));
      return;
    }
    const recaptcha = checked ? token() : "";
    if (checked && !recaptcha) {
      load();
      say(t("Tick “I'm not a robot”, then press Send."));
      return;
    }

    $form.attr("data-busy", "true");
    say(t("Sending…"));
    $.ajax({
      url: SITE.contactEndpoint,
      method: "POST",
      contentType: "application/json",
      dataType: "json",
      data: JSON.stringify({
        name: value("name"),
        surname: value("surname"),
        company: value("company"),
        message: value("message"),
        email: value("email"),
        website: value("website"),
        ...(checked ? { recaptcha } : {}),
        source: "landing",
      }),
    })
      .done((reply: { ok?: boolean }) => {
        if (!reply?.ok) {
          say(t("That did not go through. Try again, or use Email us under Contact."));
          return;
        }
        ($form.get(0) as HTMLFormElement).reset();
        say(t("Thanks. We will get back to you within 24 hours."));
      })
      .fail((xhr: JQuery.jqXHR<{ error?: string }>) => {
        const error = xhr.responseJSON?.error;
        if (error === "check") say(t("The security check did not go through. Tick “I'm not a robot” again, then press Send."));
        else if (error === "limit")
          say(t("You have sent several messages already. Try again later, or use Email us under Contact."));
        else if (error === "links")
          invalid(field("message"), t("Remove the web links from your message, or keep it to two, then press Send again."));
        else say(t("That did not go through. Try again, or use Email us under Contact."));
      })
      .always(() => {
        resetCheck();
        $form.removeAttr("data-busy");
      });
  });
}
